"use client";

import type { CSSProperties, ReactNode } from 'react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { Tip } from '@/components/fun-ui/Tip';
import { CircleChevronDownIcon, InfoIcon, MoreHorizontalIcon, XIcon } from '@/components/fun-ui/icons';

export type BenefitsCompareVariant = 'memberPlus' | 'memberPlus3x' | 'recharge';

type Row = {
  feature: string;
  basic: string;
  plus: string;
  plus3x: string;
  /** Plus 列：文案后的说明气泡（设计稿 tips） */
  plusTip?: string;
  /** Plus 3倍列：文案后的说明气泡 */
  plus3xTip?: string;
};

/** 权益表正文与 tips 图标色（与单列单元格正文一致） */
const BENEFITS_CELL_TEXT = '#3D3D3D';
/** 会员表 Plus 列前三行及「飞豆」行 tips 图标色 */
const BENEFITS_PLUS_ACCENT = '#987E61';
const PAYMENT_LEARN_MORE_URL = 'https://nfj2hhi1cw.feishu.cn/wiki/WIKtwWj2Fir5W5kpf8LcVxRLn1e';

/** 与设计稿对齐的文案 */
const BENEFIT_ROWS: Row[] = [
  {
    feature: '飞豆',
    basic: '按需购买',
    plus: '5000 飞豆/月',
    plus3x: '15000 飞豆/月',
    plusTip: '每月 Plus 会员权益飞豆和其它权益的有效期为 31 天，到期后未使用飞豆和权益自动失效回收，并自动发放下一个月的飞豆和权益。',
    plus3xTip: '每月 Plus 会员权益飞豆和其它权益的有效期为 31 天，到期后未使用飞豆和权益自动失效回收，并自动发放下一个月的飞豆和权益。',
  },
  {
    feature: '文献综述减免',
    basic: 'X',
    plus: '2000 飞豆 2 次/月',
    plus3x: '2000 飞豆 6 次/月',
  },
  {
    feature: '对照翻译减免',
    basic: 'X',
    plus: '100 飞豆 3 次/月',
    plus3x: '100 飞豆 9 次/月',
  },
  {
    feature: '文献存储上限',
    basic: '500 篇',
    plus: '2000 篇',
    plus3x: '6000 篇',
  },
  {
    feature: '购买飞豆优惠',
    basic: 'X',
    plus: '低至 5 折起',
    plus3x: '低至 5 折起',
  },
  {
    feature: '综述解析文献数',
    basic: '最多 50 篇',
    plus: '最多 100 篇',
    plus3x: '最多 100 篇',
    plusTip:
      'Max Deep 模式单次解析文献高达 100 篇，采用 10 倍算力，支持 AI 深度阅读分析，可精准控制目录和细节要求。',
    plus3xTip:
      'Max Deep 模式单次解析文献高达 100 篇，采用 10 倍算力，支持 AI 深度阅读分析，可精准控制目录和细节要求。',
  },
  {
    feature: '综述支持模式',
    basic: 'Pro、Lite',
    plus: 'Max Deep、Pro、Lite',
    plus3x: 'Max Deep、Pro、Lite',
  },
  {
    feature: '自定义 AI 表格列',
    basic: '3 个',
    plus: '15 个',
    plus3x: '15 个',
  },
  {
    feature: 'AI 表格展示列数',
    basic: '5 个',
    plus: '不限',
    plus3x: '不限',
  },
  {
    feature: '图片型文献解析',
    basic: 'X',
    plus: '支持 50 页以内',
    plus3x: '支持 50 页以内',
  },
  {
    feature: 'AI 表格导出 Excel',
    basic: 'Excel 表格',
    plus: '精装 Excel 表格',
    plus3x: '精装 Excel 表格',
  },
  {
    feature: '新权益敬请期待',
    basic: '...',
    plus: '...',
    plus3x: '...',
  },
];

function renderBenefitCell(content: string, scale: number): ReactNode {
  if (content === 'X') {
    return (
      <XIcon
        size={Math.round(16 * scale)}
        color="#E70000"
        aria-hidden
      />
    );
  }
  if (content === '...') {
    return (
      <MoreHorizontalIcon
        size={Math.round(16 * scale)}
        color={BENEFITS_CELL_TEXT}
        aria-hidden
      />
    );
  }
  return content;
}

function renderPlanCell(row: Row, column: 'plus' | 'plus3x', scale: number): ReactNode {
  const content = column === 'plus' ? row.plus : row.plus3x;
  const tip = column === 'plus' ? row.plusTip : row.plus3xTip;
  const main = renderBenefitCell(content, scale);
  if (!tip) {
    return main;
  }
  const iconSize = Math.round(10 * scale);
  const tipIconColor =
    row.feature === '飞豆' ? BENEFITS_PLUS_ACCENT : BENEFITS_CELL_TEXT;
  return (
    <span className="inline-flex max-w-full flex-wrap items-center gap-1 align-middle">
      {main}
      <Tip
        label={tip}
        side="top"
        sideOffset={6}
        className="inline-flex shrink-0 cursor-help"
        contentClassName="max-w-[min(280px,85vw)] whitespace-pre-line px-3 py-2 text-xs font-normal leading-relaxed text-primary-foreground"
      >
        <InfoIcon size={iconSize} className="block" color={tipIconColor} />
      </Tip>
    </span>
  );
}

export type PaymentBenefitsCompareTableProps = {
  scale?: number;
  variant: BenefitsCompareVariant;
  className?: string;
};

/** 竖向列分割线宽（随 scale，基准 0.9px @ 1100×759） */
function colDividerWidth(scale: number) {
  return `${0.9 * scale}px`;
}

/** 表格外轮廓圆角（设计稿 1100×760 下 10px） */
function outerCompareTableRadius(scale: number) {
  return `${10 * scale}px`;
}

/** 会员表：列竖线 — linear-gradient(90deg, …) 沿线条横向淡出 */
function memberColDividerStyle(scale: number): CSSProperties {
  const w = colDividerWidth(scale);
  return {
    backgroundImage:
      'linear-gradient(90deg, #F3E7DA 0%, rgba(243, 231, 218, 0.34) 100%)',
    backgroundSize: `${w} 100%`,
    backgroundPosition: 'left center',
    backgroundRepeat: 'no-repeat',
  };
}

/** 充值表：列竖线 0.9px #D8E1FC（设计稿 rotate 语义由竖线 border-left 实现） */
function rechargeColDividerStyle(scale: number): CSSProperties {
  return {
    borderLeftWidth: colDividerWidth(scale),
    borderLeftStyle: 'solid',
    borderLeftColor: '#D8E1FC',
  };
}

export function PaymentBenefitsCompareTable({
  scale = 1,
  variant,
  className,
}: PaymentBenefitsCompareTableProps) {
  const isMemberLayout = variant === 'memberPlus' || variant === 'memberPlus3x';

  const fsTitle = 14.4 * scale;
  const fsCell = 12.6 * scale;
  const lhTitle = 20 * scale;
  const lhCell = 18 * scale;
  const padH = `${10 * scale}px ${12 * scale}px`;
  const cellPadX = 12 * scale;
  const cellPadYDefault = 8 * scale;
  const cellPadYEmphasis = 14 * scale;
  const borderSectionMember = '#F3E7DA';
  const borderFooterMember = '#FBF7F2';
  const borderSectionRecharge = '#D8E1FC';
  const borderFooterRecharge = '#EAF0FD';

  const textDefault = '#3D3D3D';
  const textPlusTop = BENEFITS_PLUS_ACCENT;
  const sectionBreakRowIndex = 3;

  const getCellVerticalPadding = (rowIndex: number) => {
    const isFirstRow = rowIndex === 0;
    const isBeforeSectionBreak = rowIndex === sectionBreakRowIndex - 1;
    const isSectionBreakRow = rowIndex === sectionBreakRowIndex;
    const isLastRow = rowIndex === BENEFIT_ROWS.length - 1;
    return {
      top: (isFirstRow || isSectionBreakRow) ? cellPadYEmphasis : cellPadYDefault,
      bottom: (isBeforeSectionBreak || isLastRow) ? cellPadYEmphasis : cellPadYDefault,
    };
  };

  if (isMemberLayout) {
    const memberHeadCells =
      variant === 'memberPlus'
        ? [
          { key: 'plus', label: 'Plus 会员' },
          { key: 'plus3x', label: 'Plus 3倍会员' },
          { key: 'basic', label: '基础' },
        ]
        : [
          { key: 'plus3x', label: 'Plus 3倍会员' },
          { key: 'plus', label: 'Plus 会员' },
          { key: 'basic', label: '基础' },
        ];

    const headBg = '#F6EFE8';
    const headText = '#82674D';
    const col1BodyBg = 'rgba(246, 239, 232, 0.4)';

    const cellBase = (rowIndex: number): CSSProperties => {
      const sectionBreak = rowIndex === sectionBreakRowIndex;
      const cellPadding = getCellVerticalPadding(rowIndex);
      return {
        padding: `${cellPadding.top}px ${cellPadX}px ${cellPadding.bottom}px`,
        fontSize: `${fsCell}px`,
        lineHeight: `${lhCell}px`,
        color: textDefault,
        fontWeight: 400,
        backgroundColor: 'transparent',
        borderStyle: 'solid',
        borderColor: 'transparent',
        borderWidth: 0,
        borderTopWidth: sectionBreak ? colDividerWidth(scale) : 0,
        borderTopStyle: sectionBreak ? 'solid' : undefined,
        borderTopColor: sectionBreak ? borderSectionMember : 'transparent',
      };
    };

    const plusCellStyle = (rowIndex: number, withDivider: boolean): CSSProperties => {
      const top = rowIndex < 3;
      return {
        ...cellBase(rowIndex),
        ...(withDivider ? memberColDividerStyle(scale) : {}),
        color: top ? textPlusTop : textDefault,
        fontWeight: top ? 500 : 400,
      };
    };

    const learnMoreHref = PAYMENT_LEARN_MORE_URL;

    return (
      <div
        className={cn('flex w-full flex-col overflow-hidden bg-background', className)}
        style={{ borderRadius: outerCompareTableRadius(scale) }}
      >
        <div className="w-full min-w-0 shrink-0 overflow-x-hidden">
          <table
            className="w-full border-collapse"
            style={{ tableLayout: 'fixed' }}
          >
            <colgroup>
              <col style={{ width: '26%' }} />
              <col style={{ width: '28%' }} />
              <col style={{ width: '28%' }} />
              <col style={{ width: '18%' }} />
            </colgroup>
            <thead>
              <tr>
                <th
                  className="text-center"
                  style={{
                    padding: padH,
                    fontSize: `${fsTitle}px`,
                    lineHeight: `${lhTitle}px`,
                    fontWeight: 500,
                    color: headText,
                    backgroundColor: headBg,
                  }}
                >
                  权益对比
                </th>
                {memberHeadCells.map((h) => (
                  <th
                    key={h.key}
                    className={cn(
                      'text-center',
                      h.key !== 'basic' && 'whitespace-nowrap',
                    )}
                    style={{
                      padding: padH,
                      fontSize: `${fsTitle}px`,
                      lineHeight: `${lhTitle}px`,
                      fontWeight: 500,
                      color: headText,
                      backgroundColor: headBg,
                      ...memberColDividerStyle(scale),
                    }}
                  >
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {BENEFIT_ROWS.map((row, rowIndex) => {
                const td1: CSSProperties = {
                  ...cellBase(rowIndex),
                  backgroundColor: col1BodyBg,
                };
                return (
                  <tr key={row.feature}>
                    <td className="text-left" style={td1}>
                      {row.feature}
                    </td>
                    {variant === 'memberPlus' ? (
                      <>
                        <td
                          className="text-left whitespace-nowrap"
                          style={plusCellStyle(rowIndex, true)}
                        >
                          {renderPlanCell(row, 'plus', scale)}
                        </td>
                        <td
                          className="text-left whitespace-nowrap"
                          style={plusCellStyle(rowIndex, true)}
                        >
                          {renderPlanCell(row, 'plus3x', scale)}
                        </td>
                      </>
                    ) : (
                      <>
                        <td
                          className="text-left whitespace-nowrap"
                          style={plusCellStyle(rowIndex, true)}
                        >
                          {renderPlanCell(row, 'plus3x', scale)}
                        </td>
                        <td
                          className="text-left whitespace-nowrap"
                          style={plusCellStyle(rowIndex, true)}
                        >
                          {renderPlanCell(row, 'plus', scale)}
                        </td>
                      </>
                    )}
                    <td
                      className="text-left"
                      style={{
                        ...cellBase(rowIndex),
                        ...memberColDividerStyle(scale),
                      }}
                    >
                      {renderBenefitCell(row.basic, scale)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div
          className="relative z-20 w-full shrink-0 bg-background text-center pointer-events-auto"
          style={{
            padding: `${8 * scale}px ${12 * scale}px`,
            borderTop: `${colDividerWidth(scale)} solid ${borderFooterMember}`,
          }}
        >
          <Link
            href={learnMoreHref}
            target="_blank"
            className="inline-flex items-center text-[#3D3D3D] hover:text-primary hover:underline"
            style={{
              fontSize: `${fsCell}px`,
              lineHeight: `${lhCell}px`,
              fontWeight: 400,
              gap: `${4 * scale}px`,
            }}
          >
            了解更多
            <CircleChevronDownIcon
              size={Math.round(18 * scale)}
              color={textDefault}
              className="shrink-0"
              aria-hidden
            />
          </Link>
        </div>
      </div>
    );
  }

  const headBgR = '#DDE6FE';
  const headTextR = '#4E5969';
  const col1BodyBgR = 'rgba(221, 230, 254, 0.3)';

  const cellBaseR = (rowIndex: number): CSSProperties => {
    const sectionBreak = rowIndex === sectionBreakRowIndex;
    const cellPadding = getCellVerticalPadding(rowIndex);
    return {
      padding: `${cellPadding.top}px ${cellPadX}px ${cellPadding.bottom}px`,
      fontSize: `${fsCell}px`,
      lineHeight: `${lhCell}px`,
      color: textDefault,
      fontWeight: 400,
      backgroundColor: 'transparent',
      borderStyle: 'solid',
      borderColor: 'transparent',
      borderWidth: 0,
      borderTopWidth: sectionBreak ? colDividerWidth(scale) : 0,
      borderTopStyle: sectionBreak ? 'solid' : undefined,
      borderTopColor: sectionBreak ? borderSectionRecharge : 'transparent',
    };
  };

  const plusCellStyleR = (rowIndex: number, withDivider: boolean): CSSProperties => {
    const top = rowIndex < 3;
    return {
      ...cellBaseR(rowIndex),
      ...(withDivider ? rechargeColDividerStyle(scale) : {}),
      color: top ? textPlusTop : textDefault,
      fontWeight: top ? 500 : 400,
    };
  };

  const rechargeTableBg =
    'linear-gradient(180deg, #E9EFFD 0%, rgba(233, 239, 253, 0) 119%)';

  return (
    <div
      className={cn('flex h-full min-h-0 flex-col overflow-hidden bg-background', className)}
      style={{ borderRadius: outerCompareTableRadius(scale) }}
    >
      <div
        className="min-h-0 w-full flex-1 overflow-x-auto bg-background"
        style={{ backgroundImage: rechargeTableBg }}
      >
        <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '26%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '29%' }} />
            <col style={{ width: '29%' }} />
          </colgroup>
          <thead>
            <tr>
              <th
                className="text-center"
                style={{
                  padding: padH,
                  fontSize: `${fsTitle}px`,
                  lineHeight: `${lhTitle}px`,
                  fontWeight: 500,
                  color: headTextR,
                  backgroundColor: headBgR,
                }}
              >
                权益对比
              </th>
              <th
                className="text-center"
                style={{
                  padding: padH,
                  fontSize: `${fsTitle}px`,
                  lineHeight: `${lhTitle}px`,
                  fontWeight: 500,
                  color: headTextR,
                  backgroundColor: headBgR,
                  ...rechargeColDividerStyle(scale),
                }}
              >
                基础
              </th>
              <th
                className="text-center whitespace-nowrap"
                style={{
                  padding: padH,
                  fontSize: `${fsTitle}px`,
                  lineHeight: `${lhTitle}px`,
                  fontWeight: 500,
                  color: headTextR,
                  backgroundColor: headBgR,
                  ...rechargeColDividerStyle(scale),
                }}
              >
                Plus 会员
              </th>
              <th
                className="text-center whitespace-nowrap"
                style={{
                  padding: padH,
                  fontSize: `${fsTitle}px`,
                  lineHeight: `${lhTitle}px`,
                  fontWeight: 500,
                  color: headTextR,
                  backgroundColor: headBgR,
                  ...rechargeColDividerStyle(scale),
                }}
              >
                Plus 3倍会员
              </th>
            </tr>
          </thead>
          <tbody>
            {BENEFIT_ROWS.map((row, rowIndex) => {
              const td1: CSSProperties = {
                ...cellBaseR(rowIndex),
                backgroundColor: col1BodyBgR,
              };
              return (
                <tr key={row.feature}>
                  <td className="text-left" style={td1}>
                    {row.feature}
                  </td>
                  <td
                    className="text-left"
                    style={{
                      ...cellBaseR(rowIndex),
                      ...rechargeColDividerStyle(scale),
                    }}
                  >
                    {renderBenefitCell(row.basic, scale)}
                  </td>
                  <td className="text-left whitespace-nowrap" style={plusCellStyleR(rowIndex, true)}>
                    {renderPlanCell(row, 'plus', scale)}
                  </td>
                  <td className="text-left whitespace-nowrap" style={plusCellStyleR(rowIndex, true)}>
                    {renderPlanCell(row, 'plus3x', scale)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div
        className="relative z-20 w-full shrink-0 bg-transparent text-center pointer-events-auto"
        style={{
          padding: `${8 * scale}px ${12 * scale}px`,
          borderTop: `${colDividerWidth(scale)} solid ${borderFooterRecharge}`,
        }}
      >
        <Link
          href={PAYMENT_LEARN_MORE_URL}
          target="_blank"
          className="inline-flex items-center text-[#3D3D3D] hover:text-primary hover:underline"
          style={{
            fontSize: `${fsCell}px`,
            lineHeight: `${lhCell}px`,
            fontWeight: 400,
            gap: `${4 * scale}px`,
          }}
        >
          了解更多
          <CircleChevronDownIcon
            size={Math.round(18 * scale)}
            color={textDefault}
            className="shrink-0"
            aria-hidden
          />
        </Link>
      </div>
    </div>
  );
}
